import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject } from 'rxjs';
import { ActivityService } from 'src/app/main/pages/services/activity.service';
import { ActivityJson } from 'src/app/main/pages/services/microservices/json/Activity.json';
import { ActivitySimpleResponse } from 'src/app/main/pages/services/microservices/response/activitySimple.response';
import { SharedService } from 'src/app/shared/shared.service';
import { FormValidator } from 'src/app/shared/utils/form-validator';

import { UserService } from '../../../services/user.service';
import { LoadingComponent } from '../../loading/loading.component';

@Component({
  selector: 'activity-dialog',
  templateUrl: './activity-dialog.component.html',
  styleUrls: ['./activity-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    FlexLayoutModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
    MatTooltipModule,
    MatSelectModule,
    MatIconModule,
    LoadingComponent,
  ],
})
export class ActivityDialogComponent implements OnInit, OnDestroy {
  readonly dialogRef = inject(MatDialogRef<ActivityDialogComponent>);
  private fb = inject(FormBuilder);
  private fv = inject(FormValidator);
  private service = inject(ActivityService);
  private userService = inject(UserService);
  private sharedService = inject(SharedService);
  private _snackBar = inject(MatSnackBar);
  private _unsubscribeAll!: Subject<any>;

  public data: { activityId: number; taxpayerId: number } =
    inject(MAT_DIALOG_DATA);
  form!: FormGroup;
  notification: any = {};
  txtBtn = 'REGISTRAR';
  isLoading = false;
  lblActivityNumber: string[] = [
    'principal',
    'N° 2',
    'N° 3',
    'N° 4',
    'N° 5',
    'N° 6',
    'N° 7',
  ];
  actIndex = 0;

  ngOnInit(): void {
    this._unsubscribeAll = new Subject<any>();
    this.initializeForm();
    this.load();
  }

  private load(): void {
    this.get();
  }

  private get(): void {
    if (this.data.activityId > 0) {
      this.isLoading = true;
      this.service.get(this.data.activityId).subscribe({
        next: response => {
          this.form.patchValue(response.data);
          this.form.patchValue({
            regimeType: response.data.regimeType.id,
            declaration: response.data.declaration.id,
          });
          this.isLoading = false;
          this.txtBtn = 'ACTUALIZAR';
        },
        error: (err: HttpErrorResponse) => {
          this.isLoading = false;
          this.openSnack('No se encontró actividad');
        },
      });
    } else {
      this.form.patchValue({ taxpayerId: this.data.taxpayerId });
      this.sharedService.totalActivities$.subscribe(total => {
        if (total === 0) {
          this.form.controls['description']?.disable();
          this.userService.getUserEconomicActivity().subscribe({
            next: response => {
              if (response.data && response.data.length > 0) {
                this.form.patchValue({
                  description: response.data[0].economicActivityDescription,
                });
              } else {
                this.actIndex = total;
              }
              this.form.controls['description'].enable();
            },
            error: (err: HttpErrorResponse) => {
              this.actIndex = total;
              this.form.controls['description'].enable();
            },
          });
        } else {
          this.actIndex = total;
        }
      });
    }
  }

  save(): void {
    this.isLoading = true;
    let json = this.formToJson();
    this.service.save(json).subscribe({
      next: response => {
        let jsonResult = this.toJsonResult(
          response.data.id,
          json.label,
          json.isActive,
          json.mainActivity,
        );
        this.dialogRef.close(jsonResult);
        this.isLoading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.openSnack('Ups');
        this.isLoading = false;
      },
    });
  }

  //Initializers and secondary methods
  private initializeForm(): void {
    this.form = this.fb.group(
      {
        id: [0],
        taxpayerId: [0],
        description: ['', Validators.required],
        label: ['', Validators.required],
        regimeType: ['', Validators.required],
        declaration: ['', Validators.required],
        importing: ['', Validators.required],
        export: ['', Validators.required],
        isActive: [''],
      },
      { updateOn: 'blur' },
    );
    this.fieldNotificationInit();
  }

  private fieldNotificationInit(): void {
    let fields = {
      description: { required: 'Actividad principal es requerida' },
      regimeType: { required: 'Tipo de regimen es requerido' },
      declaration: { required: 'Declaracion es requerido' },
      importing: { required: 'Importador es requerido' },
      export: { required: 'Exportador es requerido' },
      label: { required: 'Etiqueta es requerida' },
    };
    this.fv.setFormValidator(this.form, this.notification, fields);
  }

  //Auxiliar methods
  controlHasError(formControlName: string): boolean {
    return this.fv.fieldHasError(formControlName);
  }

  private formToJson(): ActivityJson {
    const isActive = this.form.get('isActive')?.value;
    const json: ActivityJson = {
      id: this.form.get('id')?.value,
      taxpayerId: this.form.get('taxpayerId')?.value,
      description: this.form.get('description')?.value,
      label: this.form.get('label')?.value,
      regimeType: {
        id: this.form.get('regimeType')?.value,
        description: '',
      },
      declaration: {
        id: this.form.get('declaration')?.value,
        description: '',
      },
      importing: this.form.get('importing')?.value,
      export: this.form.get('export')?.value,
      isActive: isActive !== '' ? isActive : true,
      mainActivity: false,
    };
    return json;
  }

  private toJsonResult(
    id: number,
    label: string,
    isActive: boolean,
    mainActivity: boolean,
  ): ActivitySimpleResponse {
    return {
      id: id,
      label: label,
      isActive: isActive,
      mainActivity: mainActivity,
    };
  }

  private openSnack(message: string): void {
    this._snackBar.open(message, '', {
      verticalPosition: 'top',
      horizontalPosition: 'right',
      panelClass: ['snack-error'],
      duration: 5000,
    });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(void 0);
    this._unsubscribeAll.complete();
  }
}
